/**
 * ExportButton (Iter343) — universal export dropdown.
 *
 * Streams the current view of any "leads-like" surface as
 * XLSX · CSV · PDF · DOCX by hitting the generic
 * `GET /api/exports/leads?source=<name>&fmt=<xlsx|csv|pdf|docx>&<filters>`
 * backend endpoint.
 *
 * Props:
 *   source        · one of the SOURCES keys in `routes/exports.py`
 *   filters       · object serialised to query string (empty values pruned)
 *   testidPrefix  · scopes data-testids per host page
 *   className     · optional extra classes for the outer wrapper
 *
 * The parent owns the filter state; we only serialise it so the file the
 * user gets is a 1:1 match of what's on-screen.
 */
import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Download, ChevronDown, Loader2,
  FileSpreadsheet, FileText, FileType2, File as FileIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMATS = [
  { fmt: "xlsx", label: "Excel (.xlsx)",   icon: FileSpreadsheet, color: "#10B981" },
  { fmt: "csv",  label: "CSV",             icon: FileText,        color: "#3B82F6" },
  { fmt: "pdf",  label: "PDF (relatório)", icon: FileIcon,        color: "#DC2626" },
  { fmt: "docx", label: "Word (.docx)",    icon: FileType2,       color: "#8B5CF6" },
];

const cleanFilters = (obj) => {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "" || v === 0 || v === false) return;
    if (Array.isArray(v)) {
      if (v.length) out[k] = v.join(",");
    } else {
      out[k] = String(v);
    }
  });
  return out;
};

export default function ExportButton({
  source,
  filters,
  testidPrefix = "export",
  className = "",
  align = "right", // "right" | "left"
}) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const cleaned = useMemo(() => cleanFilters(filters), [filters]);
  const filterCount = Object.keys(cleaned).length;

  const doExport = async (fmt) => {
    setOpen(false);
    setDownloading(fmt);
    try {
      const params = new URLSearchParams({ source, fmt, ...cleaned });
      const url = `${API}/exports/leads?${params.toString()}`;
      const r = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([r.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "");
      link.download = `${source}_${stamp}.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`Export .${fmt} pronto`, {
        description: filterCount ? `Filtros aplicados: ${filterCount}` : undefined,
      });
    } catch (e) {
      toast.error(`Falha no export .${fmt}`, {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className={`relative inline-block ${className}`}
         data-testid={`${testidPrefix}-wrapper`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={!!downloading}
        data-testid={`${testidPrefix}-btn`}
        aria-expanded={open}
        aria-haspopup="menu"
        className="h-9 px-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-[12.5px] font-bold text-emerald-300 hover:border-emerald-400 hover:text-emerald-200 inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
      >
        {downloading
          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : <Download className="w-3.5 h-3.5" />}
        <span>Export</span>
        {filterCount > 0 && (
          <span className="text-[9.5px] font-bold px-1 py-0.5 rounded bg-emerald-500/25"
                data-testid={`${testidPrefix}-filter-count`}>
            {filterCount} filtro{filterCount > 1 ? "s" : ""}
          </span>
        )}
        <ChevronDown className="w-3 h-3 opacity-70" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className={`absolute ${align === "left" ? "left-0" : "right-0"} mt-1 z-50 w-56 rounded-lg border shadow-xl overflow-hidden`}
            data-testid={`${testidPrefix}-menu`}
            style={{ background: "rgba(11,17,32,0.98)", borderColor: "rgba(255,255,255,0.15)" }}
            role="menu"
          >
            {FORMATS.map(({ fmt, label, icon: Icon, color }) => (
              <button
                key={fmt}
                type="button"
                role="menuitem"
                onClick={() => doExport(fmt)}
                data-testid={`${testidPrefix}-${fmt}`}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[12.5px] text-slate-200 hover:bg-white/5 text-left"
              >
                <Icon className="w-4 h-4 shrink-0" style={{ color }} />
                <span className="flex-1">{label}</span>
              </button>
            ))}
            <div className="border-t border-white/10 px-3 py-2 text-[10.5px] text-slate-400 bg-black/30">
              {filterCount > 0
                ? `Aplicará ${filterCount} filtro${filterCount > 1 ? "s" : ""} atual${filterCount > 1 ? "is" : ""}`
                : "Sem filtros — exporta todo o universo"}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
